import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

import {
  Upload,
  X,
  AlertTriangle,
  Building2,
  User,
  FileText,
  Paperclip,
  Tag,
  Target,
  Calendar,
  CheckCircle,
  Clock,
  Truck
} from 'lucide-react';

import { ticketService } from '@services/ticketService';
import { uploadToEndpoint } from '@services/uploadService';
import { useAuth } from '@context/AuthContext';
import { useModules } from '@hooks/useModules';
import { useCategoriesByModule, useCategoryWithSubcategories } from '@hooks/useCategories';
import { useBranches } from '@hooks/useBranches';
import { useUsers } from '@hooks/useUsers';

import Button from '@components/common/Button';
import Loading from '@components/common/Loading';
import FormField from '@components/common/FormField';
import Section from '@components/common/Section';

const TicketForm = ({ mode = 'create' }) => {
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm();

  const watchedModuleId = watch('module_id');
  const watchedCategoryId = watch('category_id');

  // Use individual hooks for better caching and performance
  const { data: modules, isLoading: modulesLoading } = useModules();
  const { data: branches, isLoading: branchesLoading } = useBranches();
  const { data: users, isLoading: usersLoading } = useUsers();

  const formDataLoading = modulesLoading || branchesLoading || usersLoading;

  const { data: categories } = useCategoriesByModule(watchedModuleId);
  const { data: categoryWithSubcategories } = useCategoryWithSubcategories(watchedCategoryId);
  const subcategories = categoryWithSubcategories?.subcategories || [];

  const { data: existingTicket, isLoading: ticketLoading } = useQuery(
    ['ticket', id],
    async () => {
      return await ticketService.getTicket(id);
    },
    {
      enabled: mode === 'edit' && !!id,
      onSuccess: data => {
        reset({
          subject: data.subject,
          body: data.body,
          module_id: data.module_id,
          category_id: data.category_id,
          subcategory_id: data.subcategory_id,
          branch_id: data.branch_id,
          priority: data.priority,
          shipment_number: data.shipment_number,
          order_number: data.order_number,
          vehicle_number: data.vehicle_number
        });
        setAttachments(data.attachments || []);
      }
    }
  );

  const ticketMutation = useMutation(
    async ticketData => {
      // Clean up the data and convert to proper format
      const payload = {
        subject: ticketData.subject,
        body: ticketData.body,
        module_id: ticketData.module_id ? parseInt(ticketData.module_id) : null,
        category_id: ticketData.category_id ? parseInt(ticketData.category_id) : null,
        subcategory_id: ticketData.subcategory_id ? parseInt(ticketData.subcategory_id) : null,
        branch_id: ticketData.branch_id ? parseInt(ticketData.branch_id) : null,
        priority: ticketData.priority?.toLowerCase() || 'medium', // Keep lowercase for API consistency
        assigned_to: ticketData.assigned_to ? parseInt(ticketData.assigned_to) : null,
        customer_id: ticketData.customer_id ? parseInt(ticketData.customer_id) : null,
        supplier_id: ticketData.supplier_id ? parseInt(ticketData.supplier_id) : null,
        parent_ticket_id: ticketData.parent_ticket_id ? parseInt(ticketData.parent_ticket_id) : null,
        shipment_number: ticketData.shipment_number || null,
        order_number: ticketData.order_number || null,
        vehicle_number: ticketData.vehicle_number || null,
        estimated_resolution: ticketData.estimated_resolution || null
      };

      // Remove null/undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
          delete payload[key];
        }
      });

      let ticketResponse;
      if (mode === 'create') {
        ticketResponse = await ticketService.createTicket(payload);
      } else {
        ticketResponse = await ticketService.updateTicket(id, payload);
      }

      // Upload attachments if any exist and this is a new ticket
      if (attachments.length > 0 && mode === 'create') {
        try {
          const fileAttachments = attachments.filter(att => att instanceof File);
          if (fileAttachments.length > 0) {
            await uploadToEndpoint(
              `/tickets/${ticketResponse.id}/attachments`,
              fileAttachments
            );
          }
        } catch (uploadError) {
          console.error('Failed to upload attachments:', uploadError);
          toast.error('Ticket created but some attachments failed to upload');
        }
      }

      return ticketResponse;
    },
    {
      onSuccess: data => {
        queryClient.invalidateQueries('tickets');
        toast.success(mode === 'create' ? 'Ticket created successfully!' : 'Ticket updated successfully!');
        navigate(`/tickets/${data.id}`);
      },
      onError: error => {
        toast.error(error.response?.data?.message || `Failed to ${mode === 'create' ? 'create' : 'update'} ticket`);
      }
    }
  );

  const onSubmit = data => {
    ticketMutation.mutate(data);
  };

  const handleFileUpload = files => {
    const newFiles = Array.from(files).filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }

      const allowedTypes = [
        'image/',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument',
        'text/'
      ];
      const isAllowed = allowedTypes.some(type => file.type.startsWith(type));

      if (!isAllowed) {
        toast.error(`File ${file.name} is not supported.`);
        return false;
      }

      return true;
    });

    setAttachments(prev => [...prev, ...newFiles]);
  };

  const removeAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  if (formDataLoading || (mode === 'edit' && ticketLoading)) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-8">
        <Loading size="lg" text="Loading form..." />
      </div>
    );
  }
  return (
    <motion.div
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mx-4 my-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Form Header */}
      <div className="px-8 py-6 mt-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-600">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-primary-900">
              {mode === 'create' ? '🎫 New Support Request' : '✏️ Edit Ticket'}
            </h2>
            <p className="text-sm text-primary-700">
              {mode === 'create'
                ? 'Fill out the form below to submit your support request'
                : 'Update your ticket information'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-10">

        {/* ✅ Basic Info */}
        <Section
          title="Basic Information"
          icon={<Target className="w-5 h-5" />}
          subtitle="Essential details to categorize and prioritize your request"
          className="mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField
              label="Module"
              required
              error={errors.module_id?.message}
              helpText="Select the department or system this ticket relates to"
            >
              <div className="relative">
                <select
                  {...register('module_id', { required: 'Module is required' })}
                  className={`w-full px-4 py-3 border rounded-lg transition-all ${errors.module_id ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'} focus:ring-2 focus:ring-primary-100`}
                  onChange={e => {
                    setValue('module_id', e.target.value);
                    setValue('category_id', '');
                    setValue('subcategory_id', '');
                  }}
                >
                  <option value="">Choose a module...</option>
                  {modules?.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.name}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>

            <FormField
              label="Priority Level"
              required
              error={errors.priority?.message}
              helpText="How urgent is this request?"
            >
              <div className="relative">
                <select
                  {...register('priority', { required: 'Priority is required' })}
                  className={`w-full px-4 py-3 border rounded-lg transition-all ${errors.priority ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'
                    } focus:ring-2 focus:ring-primary-100`}
                >
                  <option value="">Select priority level...</option>
                  <option value="low" className="text-green-600">🟢 Low - Can wait</option>
                  <option value="medium" className="text-yellow-600">🟡 Medium - Normal priority</option>
                  <option value="high" className="text-orange-600">🟠 High - Important</option>
                  <option value="critical" className="text-red-600">🔴 Critical - Urgent</option>
                </select>
              </div>
            </FormField>
          </div>
        </Section>
        {/* ✅ Category & Assignee */}
        <Section
          title="Classification & Routing"
          icon={<CheckCircle className="w-5 h-5" />}
          subtitle="Help us route your ticket to the right team"
          className="mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField
              label="Category"
              required
              error={errors.category_id?.message}
              helpText="Specific type of issue or request"
            >
              <select
                {...register('category_id', {
                  required: 'Category is required'
                })}
                disabled={!watchedModuleId}
                className={`w-full px-4 py-3 border rounded-lg transition-all ${errors.category_id ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'
                  } ${!watchedModuleId ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-primary-100'}`}
                onChange={e => {
                  setValue('category_id', e.target.value);
                  setValue('subcategory_id', '');
                }}
              >
                <option value="">
                  {watchedModuleId ? 'Choose a category...' : '👈 Select a module first'}
                </option>
                {categories?.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Subcategory"
              helpText="More specific classification (optional)"
            >
              <select
                {...register('subcategory_id')}
                disabled={!watchedCategoryId}
                className={`w-full px-4 py-3 border rounded-lg transition-all ${!watchedCategoryId ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100'
                  }`}
              >
                <option value="">
                  {watchedCategoryId
                    ? 'Choose subcategory (optional)...'
                    : '👈 Select a category first'}
                </option>
                {subcategories?.map(subcategory => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Branch Location"
              helpText="Which office or branch is this related to?"
            >
              <div className="relative">
                <select
                  {...register('branch_id')}
                  className="w-full px-4 py-3 border rounded-lg transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">Choose branch (optional)...</option>
                  {branches?.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      🏢 {branch.name} - {branch.city}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>

            {user?.role === 'ADMIN' && (
              <FormField
                label="Assign To Team Member"
                helpText="Leave empty for automatic assignment"
              >
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    {...register('assigned_to')}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="">🤖 Auto-assign to best available team member</option>
                    {users
                      ?.filter(u => u.role !== 'customer')
                      .map(u => (
                        <option key={u.id} value={u.id}>
                          👤 {u.first_name} {u.last_name} - {u.department}
                        </option>
                      ))}
                  </select>
                </div>
              </FormField>
            )}
          </div>
        </Section>
        {/* ✅ Ticket Details */}
        <Section
          title="Request Details"
          icon={<FileText className="w-5 h-5" />}
          subtitle="Tell us about your issue or request in detail"
          className="mb-8"
        >
          <FormField
            label="Subject Line"
            required
            error={errors.subject?.message}
            helpText="Brief, clear summary of your request"
          >
            <input
              type="text"
              {...register('subject', {
                required: 'Subject is required'
              })}
              placeholder="e.g., Login issue on dashboard, Request new software license..."
              className={`w-full !h-10 px-4 border rounded-lg shadow-sm transition-all text-gray-900 ${errors.subject
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-300 focus:border-primary-500'} focus:ring-2 focus:ring-primary-100`}
            />
          </FormField>

          <FormField
            label="Detailed Description"
            required
            error={errors.body?.message}
            className="mt-6"
            helpText="Provide as much context as possible - steps to reproduce, error messages, expected vs actual behavior"
          >
            <textarea
              {...register('body', {
                required: 'Description is required',
                minLength: {
                  value: 10,
                  message: 'Description must be at least 10 characters'
                }
              })}
              rows={6}
              placeholder={`Please provide detailed information about your request:
• What were you trying to do?
• What happened instead?
• Any error messages?
• Any other relevant details...`}
              className={`w-full !px-5 py-4 border rounded-lg resize-vertical transition-all text-gray-900 ${errors.body ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'} focus:ring-2 focus:ring-primary-100`}
            />
          </FormField>
        </Section>

        {/* ✅ Additional Information */}
        <Section
          title="Related Information"
          icon={<Truck className="w-5 h-5" />}
          subtitle="Reference numbers or identifiers that might help us assist you"
          className="mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FormField
              label="Shipment Number"
              helpText="If related to a specific shipment"
            >
              <input
                type="text"
                {...register('shipment_number')}
                placeholder="e.g., SHP-2025-001"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </FormField>

            <FormField
              label="Order Number"
              helpText="Customer or internal order reference"
            >
              <input
                type="text"
                {...register('order_number')}
                placeholder="e.g., ORD-2025-0123"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </FormField>

            <FormField
              label="Vehicle Number"
              helpText="If related to a specific vehicle"
            >
              <input
                type="text"
                {...register('vehicle_number')}
                placeholder="e.g., MH-12-XY-5678"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </FormField>
          </div>
        </Section>
        {/* ✅ Attachments */}
        <Section
          title="Supporting Files"
          icon={<Upload className="w-5 h-5" />}
          subtitle="Screenshots, documents, or other files that might help us understand your request"
          className="mb-8"
        >
          <div
            className={`border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 cursor-pointer ${isDragging
              ? 'border-primary-400 bg-primary-50 scale-102'
              : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <div className={`transition-all !py-4 duration-200 ${isDragging ? 'scale-110' : ''}`}>
              <Upload className={`mx-auto h-12 w-12 mb-4 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700 mb-1">
                {isDragging ? '📁 Drop your files here!' : '📎 Add supporting files'}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                📸 Screenshots • 📄 Documents • 📊 Spreadsheets • 📝 Text files
                <br />
                <span className="text-xs text-gray-400">Maximum 10MB per file</span>
              </p>
              <Button
                type="button"
                variant="outline"
                size="lg"
                leftIcon={<Paperclip className="w-5 h-5" />}
                className="px-6 !mb-3"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('file-upload')?.click();
                }}
              >
                Browse Files
              </Button>
            </div>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              id="file-upload"
              className="hidden"
              onChange={e => handleFileUpload(e.target.files)}
            />
          </div>

          {attachments.length > 0 && (
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-4">
                <FileText className="w-4 h-4" />
                <span>Attached Files ({attachments.length})</span>
              </div>
              {attachments.map((file, index) => (
                <motion.div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:shadow-sm transition-all mb-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-lg border">
                      <FileText className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        📄 {file.name || file.original_filename}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-2">
                        {file.size
                          ? `💾 ${(file.size / 1024 / 1024).toFixed(2)} MB`
                          : '📁 Existing file'}
                        {file.type && (
                          <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                            {file.type.split('/')[1]?.toUpperCase()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </Section>

        {/* ✅ Form Actions */}
        <div className="p-8 mt-10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 !my-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span><span className="text-red-500 font-medium">*</span> Required fields</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/tickets')}
                leftIcon={<X className="w-4 h-4" />}
              >
                Cancel
              </Button>
              {/* <Button
                type="button"
                variant="outline"
                onClick={() => reset()}
                className="text-gray-600 border-gray-300"
              >
                Reset Form
              </Button> */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={ticketMutation.isLoading}
                disabled={ticketMutation.isLoading}
                leftIcon={ticketMutation.isLoading ? <Clock className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                className="px-8 py-3 hover:from-primary-700 hover:to-primary-800"
              >
                {mode === 'create'
                  ? ticketMutation.isLoading
                    ? 'Creating Your Ticket...'
                    : '🎫 Create Ticket'
                  : ticketMutation.isLoading
                    ? 'Saving Changes...'
                    : '💾 Update Ticket'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default TicketForm;